import { useEffect, useId, useRef } from 'react';
import { AlertCircle, Sparkles } from 'lucide-react';
import { SocialPlatform, socialPlatformLabel } from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';

/** A platform as the AI cards name it: "X", not the label's "X / Twitter". */
export function convertPlatformName(platform: SocialPlatform): string {
  return platform === SocialPlatform.X ? 'X' : socialPlatformLabel[platform];
}

/** "1 character", "461 characters": the count as the counter writes it. */
export function characterCount(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'character' : 'characters'}`;
}

/** The success-tinted note: the AI's colour, "clearly a suggestion and not an error". */
const SUGGESTION_BOX =
  'border-success/20 bg-success-tint/70 dark:border-success-dark/30 dark:bg-success-dark-tint/70';
/** The app's error box (AlertBanner's error tone), for the card once a conversion failed. */
const ERROR_BOX = 'border-red-200 bg-red-50 dark:border-error-dark/30 dark:bg-error-dark-tint';

/**
 * The AI "Convert to thread" card, right under the text box of a single X or Threads post whose
 * text is over the platform's limit. Three states:
 * - `offer`: how far over the post is, and "Convert to thread with AI" or "Keep as a single post";
 * - `converting`: the request is away - a disabled "Converting..." and Cancel;
 * - `failed`: it did not work - Retry, or "Keep as a single post" - with the server's reason when it
 *   gave one (`detail`: a text Retry cannot help with).
 *
 * The card says and offers; what each button does is the caller's. It moves focus itself only when
 * its state changes under the person - to Cancel once converting, to Retry once failed, back to
 * "Convert to thread with AI" after a Cancel - and only while focus is in the card or nowhere (the
 * button that had it went away); drawn fresh while converting or failed with focus nowhere (after
 * Regenerate), it puts focus on Cancel or Retry. Focus anywhere else is never taken.
 */
export function ThreadConvertCard({
  state,
  platform,
  count,
  limit,
  detail,
  busy = false,
  onConvert,
  onCancel,
  onRetry,
  onKeep,
}: {
  state: 'offer' | 'converting' | 'failed';
  platform: SocialPlatform;
  /** The post's length as the platform counts it. */
  count: number;
  limit: number;
  /** Failed: the server's reason, said under the error, or nothing. */
  detail?: string | null;
  /**
   * A save, a publish or an AI Assist draft is on its way: what Convert and Retry bring replaces
   * the post's text, so they wait for it, as the AI Assist waits for a conversion. Cancel and
   * "Keep as a single post" change no text.
   */
  busy?: boolean;
  onConvert: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onKeep: () => void;
}) {
  const titleId = useId();
  const root = useRef<HTMLDivElement>(null);
  const convert = useRef<HTMLButtonElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const retry = useRef<HTMLButtonElement>(null);
  const shown = useRef<typeof state | null>(null);

  useEffect(() => {
    const before = shown.current;
    if (before === state) return;
    shown.current = state;
    const active = document.activeElement;
    const nowhere = active == null || active === document.body;
    const inCard = !nowhere && root.current?.contains(active) === true;
    if (before === null ? !nowhere : !(nowhere || inCard)) return;
    const target =
      state === 'converting'
        ? cancel
        : state === 'failed'
          ? retry
          : // A fresh suggestion card leaves focus to the editor (its text box).
            before === null
            ? null
            : convert;
    target?.current?.focus();
  }, [state]);

  const failed = state === 'failed';
  const name = convertPlatformName(platform);
  return (
    <div
      ref={root}
      role="group"
      aria-labelledby={titleId}
      className={cn('rounded-xl border px-4 py-3', failed ? ERROR_BOX : SUGGESTION_BOX)}
    >
      <div className="flex items-start gap-3">
        {failed ? (
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500 dark:text-error-dark" aria-hidden />
        ) : (
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-success dark:text-success-dark" aria-hidden />
        )}
        <div className="min-w-0 flex-1 text-sm">
          {state === 'offer' && (
            <>
              <p id={titleId} className="font-semibold text-ink-strong">
                {`This post is ${characterCount(count - limit)} over the ${name} limit of ${limit.toLocaleString('en-US')}`}
              </p>
              <p className="mt-0.5 text-ink">
                Convert it to a thread? AI will split your text into connected posts that cover
                everything you wrote. It keeps your wording and only makes minor edits so each post
                fits and reads naturally. You can review and edit every post before saving.
              </p>
            </>
          )}
          {state === 'converting' && (
            <>
              <p id={titleId} className="font-semibold text-ink-strong">
                Converting your text into a thread
              </p>
              <p className="mt-0.5 text-ink">
                {`Splitting it into posts of ${limit.toLocaleString('en-US')} characters or fewer and making minor edits so each one reads naturally.`}
              </p>
            </>
          )}
          {failed && (
            <div role="alert" className="text-red-700 dark:text-error-dark">
              <p id={titleId} className="font-semibold">
                We couldn&apos;t convert your text. Try again.
              </p>
              {detail && <p className="mt-0.5">{detail}</p>}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {state === 'offer' && (
              <>
                <Button ref={convert} size="sm" disabled={busy} onClick={onConvert}>
                  <Sparkles className="h-4 w-4" aria-hidden />
                  Convert to thread with AI
                </Button>
                <Button variant="secondary" size="sm" onClick={onKeep}>
                  Keep as a single post
                </Button>
              </>
            )}
            {state === 'converting' && (
              <>
                <Button size="sm" disabled loading>
                  Converting...
                </Button>
                <Button ref={cancel} variant="secondary" size="sm" onClick={onCancel}>
                  Cancel
                </Button>
              </>
            )}
            {failed && (
              <>
                <Button ref={retry} size="sm" disabled={busy} onClick={onRetry}>
                  Retry
                </Button>
                <Button variant="secondary" size="sm" onClick={onKeep}>
                  Keep as a single post
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
